import React, { createContext, useCallback, useContext, useState } from 'react';

import { CustomFile } from '../../Types/file';
import { getFilesInFolder } from '../../util/FolderPathUtil';

export interface FileFolderSelectionContextType {
  selectedFiles: CustomFile[];
  selectedFolder: string | null;
  handleFileSelect: (file: CustomFile) => void;
  handleFolderSelect: (folder: string | null, allFiles: CustomFile[]) => void;
  clearSelection: () => void;
}

const FileFolderSelectionContext = createContext<FileFolderSelectionContextType | undefined>(
  undefined
);

export const FileFolderSelectionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<CustomFile[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const handleFileSelect = useCallback((file: CustomFile) => {
    setSelectedFiles((prev) =>
      prev.some((f) => f.id === file.id) ? prev.filter((f) => f.id !== file.id) : [...prev, file]
    );
  }, []);

  const handleFolderSelect = useCallback((folder: string | null, allFiles: CustomFile[]) => {
    if (!folder) {
      setSelectedFolder(null);
      setSelectedFiles([]);
      return;
    }

    const filesInFolder = getFilesInFolder(folder, allFiles);
    setSelectedFolder(folder);
    setSelectedFiles(filesInFolder);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
    setSelectedFolder(null);
  }, []);

  const value: FileFolderSelectionContextType = {
    selectedFiles,
    selectedFolder,
    handleFileSelect,
    handleFolderSelect,
    clearSelection,
  };

  return (
    <FileFolderSelectionContext.Provider value={value}>
      {children}
    </FileFolderSelectionContext.Provider>
  );
};

export const useFileFolderSelection = () => {
  const context = useContext(FileFolderSelectionContext);
  if (!context) {
    throw new Error('useFileFolderSelection must be used within a FileFolderSelectionProvider');
  }
  return context;
};
